import { MakeApiCall } from "../apis";

/** user_data keys used by the trial banner, billing UI, and feature locks. */
const SUBSCRIPTION_USER_DATA_KEYS = [
  "has_subscription",
  "has_trial",
  "is_trial_expired",
  "remaining_trial_days",
  "trial_expires_at",
  "subscription",
];

const pickSubscriptionFields = (source) => {
  if (!source || typeof source !== "object") return {};
  const patch = {};
  SUBSCRIPTION_USER_DATA_KEYS.forEach((key) => {
    if (source[key] !== undefined) {
      patch[key] = source[key];
    }
  });
  return patch;
};

const mapBillingDataToUserData = (billingData) => {
  if (!billingData) return {};

  const patch = {};
  if (billingData.has_subscription !== undefined) {
    patch.has_subscription = billingData.has_subscription;
  }

  const sub = billingData.subscription;
  if (sub) {
    if (sub.is_trialing) {
      patch.has_trial = true;
      patch.is_trial_expired = false;
      if (sub.remaining_days !== undefined) {
        patch.remaining_trial_days = sub.remaining_days;
      }
    } else if (sub.is_active) {
      patch.has_trial = false;
      patch.is_trial_expired = false;
    }

    if (sub.next_due_on !== undefined) {
      patch.subscription = {
        ...(patch.subscription || {}),
        ends_at: sub.next_due_on,
      };
    }
  }

  return patch;
};

const applyPatchToSession = (userData, patch, setData) => {
  if (!patch || Object.keys(patch).length === 0) return false;
  if (!userData?.user?.user_data || typeof setData !== "function") return false;

  setData({
    ...userData,
    user: {
      ...userData.user,
      user_data: {
        ...userData.user.user_data,
        ...patch,
      },
    },
  });
  return true;
};

/**
 * Refresh subscription/trial fields in Redux after billing changes.
 * Prefers verify-payment response user_data, then GET user/{id}, then billing/my-subscription mapping.
 *
 * @param {object} userData - Current Redux session
 * @param {function} setData - Redux setData dispatcher
 * @param {object} [options]
 * @param {object} [options.verifyResponse] - billing/verify-payment or verify-plan-change response
 * @param {object} [options.billingData] - billing/my-subscription payload (fallback)
 * @returns {Promise<{ success: boolean }>}
 */
export const refreshUserSubscriptionState = async (userData, setData, options = {}) => {
  const { verifyResponse, billingData } = options;
  const userId = userData?.user?.user_data?.id;
  const token = userData?.token;

  if (!userId || !token || typeof setData !== "function") {
    return { success: false };
  }

  // 1. Use updated user_data from verify-payment response when available
  let verifyPatch = pickSubscriptionFields(verifyResponse?.data?.user_data);
  if (Object.keys(verifyPatch).length === 0) {
    verifyPatch = pickSubscriptionFields(verifyResponse?.data?.user?.user_data);
  }
  if (Object.keys(verifyPatch).length > 0) {
    return { success: applyPatchToSession(userData, verifyPatch, setData) };
  }

  // 2. Fetch fresh profile from GET user/{id}
  try {
    const response = await MakeApiCall(`user/${userId}`, "get", null, true, {}, token);
    if (response?.status && response?.data) {
      const profilePatch = pickSubscriptionFields(response.data);
      if (Object.keys(profilePatch).length > 0) {
        return { success: applyPatchToSession(userData, profilePatch, setData) };
      }
    }
  } catch (err) {
    console.error("refreshUserSubscriptionState: failed to fetch user profile", err);
  }

  // 3. Fall back to billing/my-subscription shape
  const billingPatch = mapBillingDataToUserData(billingData);
  if (Object.keys(billingPatch).length > 0) {
    return { success: applyPatchToSession(userData, billingPatch, setData) };
  }

  return { success: false };
};
